// ── Time Grid Geometry ──
// Pure conversions between zoned instants and vertical pixel positions on a
// day column. Shared by render, DnD, resize, and note drag-create.
//
// All geometry uses the date's display-zone wall clock. Each date keeps the
// same 24-hour axis; equal Y positions across differently zoned columns mean
// equal local wall times, not equal instants.

import { Temporal } from "temporal-polyfill";
import type { ZonedTimeString } from "@poi-planner/shared";
import { zonedFromDateAndTime } from "./zonedTime";

// ── Constants ──

/** Vertical scale: 0.8px per minute → 48px per hour. */
export const PX_PER_MINUTE = 0.8;
export const HOUR_HEIGHT_PX = 60 * PX_PER_MINUTE; // 48

/** Snap granularity for pointer vs touch gestures. */
export const SNAP_MINUTES_POINTER = 5;
export const SNAP_MINUTES_TOUCH = 15;

/** Smallest block span a gesture can produce. */
export const MIN_SPAN_MINUTES = 15;

/** Where the grid scrolls to when a day has no blocks (UI constant). */
export const DEFAULT_SCROLL_HOUR = 8;

// ── Day anchors ──

/** Start-of-day instant for a date in a zone (DST-safe). */
export function dayStart(date: string, zone: string): Temporal.ZonedDateTime {
  return Temporal.PlainDate.from(date).toZonedDateTime(zone);
}

/** Length of the shared local wall-clock axis. */
export function dayLengthMinutes(date: string, zone: string): number {
  // Validate both inputs while keeping the geometry deliberately fixed.
  dayStart(date, zone);
  return 24 * 60;
}

/** Pixel height of a day column. */
export function columnHeightPx(date: string, zone: string): number {
  return dayLengthMinutes(date, zone) * PX_PER_MINUTE;
}

// ── Instant ↔ Y conversions ──

/**
 * Vertical position (px from the column top) of a zoned instant within the
 * column for `date`/`zone`. Instants before/after the day map to negative /
 * beyond-height values — callers clip.
 */
export function instantToY(
  instant: ZonedTimeString,
  date: string,
  zone: string,
): number {
  const display = Temporal.ZonedDateTime.from(instant).withTimeZone(zone);
  const dayOffset = Temporal.PlainDate.from(date).until(
    display.toPlainDate(),
  ).days;
  const minutes =
    dayOffset * 24 * 60 +
    display.hour * 60 +
    display.minute +
    display.second / 60;
  return minutes * PX_PER_MINUTE;
}

/** Vertical position for a local wall-clock time on a date/zone column. */
export function wallTimeToY(time: string, date: string, zone: string): number {
  return instantToY(zonedFromDateAndTime(date, time, zone), date, zone);
}

/** Round minutes to the nearest snap increment. */
export function snapMinutes(minutes: number, snap: number): number {
  return Math.round(minutes / snap) * snap;
}

/**
 * Zoned time for a vertical position within the column for `date`/`zone`,
 * snapped to `snap` minutes. Clamped to the day (so a drop at the very bottom
 * edge stays on the date).
 */
export function yToZonedTime(
  y: number,
  date: string,
  zone: string,
  snap: number = SNAP_MINUTES_POINTER,
): ZonedTimeString {
  const dayLength = 24 * 60;
  // Largest snap-aligned minute-of-day strictly below dayLength, so the
  // result never rolls onto the next date (a caller that wants a different
  // date passes a different `date`/`zone`, detected from the actual pointed-
  // at column — see CalendarDayColumn/useBlockResize).
  const maxMinuteOfDay = Math.floor((dayLength - 1) / snap) * snap;
  const minuteOfDay = Math.max(
    0,
    Math.min(snapMinutes(y / PX_PER_MINUTE, snap), maxMinuteOfDay),
  );
  const hour = Math.floor(minuteOfDay / 60);
  const minute = minuteOfDay % 60;
  return Temporal.PlainDate.from(date)
    .toPlainDateTime({ hour, minute })
    .toZonedDateTime(zone, { disambiguation: "compatible" })
    .toString();
}

// ── Block geometry ──

export interface BlockGeometry {
  top: number;
  height: number;
  /** Block starts before this column's midnight (continues from previous day) */
  clippedTop: boolean;
  /** Block ends after this column's midnight (continues into next day) */
  clippedBottom: boolean;
}

export interface BlockContinuation {
  /** This rendered segment continues from an earlier logical trip date. */
  fromPreviousDay: boolean;
  /** This rendered segment continues into a later logical trip date. */
  intoNextDay: boolean;
}

/** Minimum rendered height so micro-blocks stay legible/clickable. */
export const MIN_BLOCK_HEIGHT_PX = MIN_SPAN_MINUTES * PX_PER_MINUTE;

/**
 * Positions a block on the column for `date`/`zone`, clipping at the column
 * boundaries. Cross-midnight blocks clip and report which edge continues so
 * the renderer can show a continuation indicator.
 */
export function blockGeometry(
  startTime: ZonedTimeString,
  endTime: ZonedTimeString,
  date: string,
  zone: string,
  continuation?: BlockContinuation,
): BlockGeometry {
  const columnHeight = columnHeightPx(date, zone);
  const rawTop = instantToY(startTime, date, zone);
  const rawBottom = instantToY(endTime, date, zone);

  // A logical trip-day boundary can differ from midnight in the current
  // column's zone when the next date has a timezone override. Segment
  // metadata therefore takes precedence over endpoint conversion: a
  // continuation fills to/from the visual day edge even when the event's
  // absolute endpoint converts to a wall time inside this date.
  const clippedTop = continuation?.fromPreviousDay || rawTop < 0;
  const clippedBottom = continuation?.intoNextDay || rawBottom > columnHeight;
  const top = clippedTop ? 0 : Math.max(0, Math.min(rawTop, columnHeight));
  const bottom = clippedBottom
    ? columnHeight
    : Math.max(top, Math.min(rawBottom, columnHeight));

  return {
    top,
    height: Math.max(bottom - top, MIN_BLOCK_HEIGHT_PX),
    clippedTop,
    clippedBottom,
  };
}
